// HU-02/HU-04: unirse a un conjunto con el código del afiche (o el QR que lleva a /unirme?codigo=…).

import { useEffect, useState, type FormEvent } from "react";
import { useNavigate, useSearchParams } from "react-router";
import { VINCULOS_UNIDAD, type VinculoUnidad } from "@porteria/core";
import { llamar } from "../firebase";
import { Boton, Campo, Opciones, Pantalla, Tarjeta, useAccion, useAvisos } from "../ui";
import { Icono } from "../iconos";
import { rutaDelRol } from "./Inicio";

interface Encontrado {
  id: string;
  nombre: string;
  ciudad: string;
  torres: { nombre: string; unidades: { id: string; numero: string; piso: number | null }[] }[];
}


export default function Unirme() {
  const [parametros] = useSearchParams();
  const navegar = useNavigate();
  const { avisar } = useAvisos();
  const { ejecutar, ocupado } = useAccion();
  const [codigo, setCodigo] = useState(parametros.get("codigo")?.toUpperCase() ?? "");
  const [conjunto, setConjunto] = useState<Encontrado | null>(null);
  // La app es para residentes. Vigilantes y domiciliarios no se registran solos: la administración les crea la cuenta.
  const [rol, setRol] = useState<"RESIDENTE" | "ADMIN">("RESIDENTE");
  const [torre, setTorre] = useState("");
  const [unidadId, setUnidadId] = useState("");
  const [vinculoUnidad, setVinculoUnidad] = useState<VinculoUnidad>("INQUILINO");

  const buscar = async (e?: FormEvent) => {
    e?.preventDefault();
    const r = await ejecutar("buscar", () => llamar<Encontrado>("buscarConjunto", { codigo: codigo.trim() }));
    if (r) setConjunto(r);
  };

  useEffect(() => {
    if (parametros.get("codigo")) buscar();
    // Solo al abrir desde el QR del afiche.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const unidades = conjunto?.torres.find((t) => t.nombre === torre)?.unidades ?? [];

  const solicitar = async () => {
    if (!conjunto) return;
    const r = await ejecutar("solicitar", () =>
      llamar<{ estado: string; conjuntoId: string }>("solicitarMembresia", {
        codigo: codigo.trim(),
        rol,
        unidadId: rol === "RESIDENTE" ? unidadId : undefined,
        vinculoUnidad: rol === "RESIDENTE" ? vinculoUnidad : undefined,
      }),
    );
    if (!r) return;
    if (r.estado === "APROBADO") {
      avisar("¡Listo! La administración ya te había autorizado.");
      navegar(rutaDelRol(r.conjuntoId, rol), { replace: true });
    } else {
      avisar(
        rol === "ADMIN"
          ? "Solicitud enviada. El dueño de la plataforma la revisará."
          : rol === "RESIDENTE"
            ? "Solicitud enviada. Si alguien de tu apartamento ya usa la app, puede aprobarte al instante; si no, la administración."
            : "Solicitud enviada. Te avisamos cuando la administración la apruebe.",
      );
      navegar("/inicio?elegir", { replace: true });
    }
  };

  const listo = rol === "ADMIN" || !!unidadId;

  return (
    <Pantalla titulo="Unirme a un conjunto" volver="/inicio?elegir">
      <Tarjeta>
        <form className="pila" onSubmit={buscar}>
          <Campo etiqueta="Código del conjunto" ayuda="Son 6 letras y números. Está en el afiche de la portería.">
            <input
              value={codigo}
              onChange={(e) => {
                setCodigo(e.target.value.toUpperCase());
                setConjunto(null);
              }}
              className="mono grande"
              maxLength={12}
              autoCapitalize="characters"
              autoComplete="off"
              placeholder="ABC123"
            />
          </Campo>
          {!conjunto && (
            <Boton type="submit" ocupado={ocupado === "buscar"} disabled={codigo.trim().length < 4}>
              Buscar
            </Boton>
          )}
        </form>
        {conjunto && (
          <p className="aviso-linea aviso-ok fila" style={{ gap: 8 }}>
            <Icono nombre="checkCirculo" /> {conjunto.nombre} · {conjunto.ciudad}
          </p>
        )}
      </Tarjeta>

      {conjunto && rol === "RESIDENTE" && (
        <Tarjeta>
          <h2>Tu apartamento</h2>
          {conjunto.torres.length === 0 ? (
            <p className="aviso-linea aviso-espera">La administración aún no ha cargado los apartamentos. Intenta más tarde.</p>
          ) : (
            <div className="fila" style={{ alignItems: "flex-start" }}>
              <Campo etiqueta="Torre">
                <select
                  value={torre}
                  onChange={(e) => {
                    setTorre(e.target.value);
                    setUnidadId("");
                  }}
                >
                  <option value="">Elige…</option>
                  {conjunto.torres.map((t) => (
                    <option key={t.nombre} value={t.nombre}>
                      {t.nombre}
                    </option>
                  ))}
                </select>
              </Campo>
              <Campo etiqueta="Apartamento">
                <select value={unidadId} onChange={(e) => setUnidadId(e.target.value)} disabled={!torre}>
                  <option value="">Elige…</option>
                  {unidades.map((u) => (
                    <option key={u.id} value={u.id}>
                      {u.numero}
                    </option>
                  ))}
                </select>
              </Campo>
            </div>
          )}
          <Campo etiqueta="¿Cuál es tu relación con el apartamento?" grupo>
            <Opciones
              nombre="Relación con el apartamento"
              valor={vinculoUnidad}
              alCambiar={setVinculoUnidad}
              opciones={Object.entries(VINCULOS_UNIDAD).map(([valor, texto]) => ({ valor: valor as VinculoUnidad, texto }))}
            />
          </Campo>
        </Tarjeta>
      )}

      {conjunto && rol === "ADMIN" && (
        <Tarjeta>
          <h2>Acceso de la administración</h2>
          <p className="chico">El dueño de la plataforma revisa tu solicitud. Cuando la apruebe, entras al panel del conjunto.</p>
        </Tarjeta>
      )}

      {conjunto && (
        <>
          <Boton grande disabled={!listo} ocupado={ocupado === "solicitar"} onClick={solicitar}>
            Enviar solicitud
          </Boton>
          <p className="chico texto-suave centro">
            ¿Trabajas en la portería o llevas paquetes? La administración del conjunto te crea la cuenta: pídesela.
          </p>
          <button type="button" className="boton boton-texto" onClick={() => setRol(rol === "ADMIN" ? "RESIDENTE" : "ADMIN")}>
            {rol === "ADMIN" ? "Soy residente" : "Soy de la administración"}
          </button>
        </>
      )}
    </Pantalla>
  );
}
